import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, BarChart3, Download, Eye, Calendar, Package, AlertTriangle, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import InventoryService from '@/api/inventory.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface InventoryReport {
  id: number;
  publicId: string;
  businessId: number;
  reportName: string;
  reportType: 'valuation' | 'aging' | 'reorder' | 'movement' | 'abc_analysis' | 'custom';
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
  startDate: string;
  endDate: string;
  status: 'generating' | 'completed' | 'failed';
  generatedBy?: string;
  fileUrl?: string;
  summary?: {
    totalItems: number;
    totalValue: number;
    lowStockItems: number;
    outOfStockItems: number;
  };
  dateCreated: string;
  dateUpdated: string;
}

const InventoryReports: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reports, setReports] = useState<InventoryReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchReports();
  }, [businessId]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await InventoryService.getInventoryReports(businessId);
      
      if (response.data.result === 'success') {
        setReports(response.data?.reports || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch inventory reports",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching inventory reports:', error);
      toast({
        title: "Error",
        description: "Failed to fetch inventory reports",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (reportType: string, period: string = 'monthly') => {
    if (!canCreate('Inventory')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to generate reports",
        variant: "destructive",
      });
      return;
    }

    try {
      await InventoryService.generateInventoryReport(businessId, {
        reportType,
        period,
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
      });
      toast({
        title: "Success",
        description: "Report generation started. You'll be notified when it's ready.",
      });
      fetchReports();
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate report",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async (report: InventoryReport) => {
    if (!report.fileUrl) {
      toast({
        title: "Error",
        description: "Report file not available",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await InventoryService.downloadInventoryReport(report.publicId);
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.reportName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: "Error",
        description: "Failed to download report",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      generating: { variant: 'secondary' as const, label: 'Generating' },
      completed: { variant: 'default' as const, label: 'Completed' },
      failed: { variant: 'destructive' as const, label: 'Failed' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.generating;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeLabel = (type: string) => {
    const typeLabels = {
      valuation: 'Inventory Valuation',
      aging: 'Inventory Aging',
      reorder: 'Reorder Report',
      movement: 'Movement Analysis',
      abc_analysis: 'ABC Analysis',
      custom: 'Custom Report'
    };
    return typeLabels[type as keyof typeof typeLabels] || type;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.reportName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || report.reportType === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory Reports</h1>
          <p className="text-muted-foreground">
            Generate and view inventory analysis reports
          </p>
        </div>
        {canCreate('Inventory') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Generate Report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Report Types</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleGenerateReport('valuation')}>
                <BarChart3 className="mr-2 h-4 w-4" />
                Inventory Valuation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateReport('aging')}>
                <TrendingDown className="mr-2 h-4 w-4" />
                Inventory Aging
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateReport('reorder')}>
                <AlertTriangle className="mr-2 h-4 w-4" />
                Reorder Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateReport('movement')}>
                <Package className="mr-2 h-4 w-4" />
                Movement Analysis
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateReport('abc_analysis')}>
                <BarChart3 className="mr-2 h-4 w-4" />
                ABC Analysis
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/inventory/reports/create')}>
                <Plus className="mr-2 h-4 w-4" />
                Custom Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredReports.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredReports.filter(report => report.status === 'completed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredReports.filter(report => {
                const reportDate = new Date(report.dateCreated);
                const now = new Date();
                return reportDate.getMonth() === now.getMonth() && reportDate.getFullYear() === now.getFullYear();
              }).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredReports.length > 0 ? Math.round(
                filteredReports.reduce((sum, report) => sum + (report.summary?.totalItems || 0), 0) / filteredReports.length
              ) : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Types</option>
              <option value="valuation">Valuation</option>
              <option value="aging">Aging</option>
              <option value="reorder">Reorder</option>
              <option value="movement">Movement</option>
              <option value="abc_analysis">ABC Analysis</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory Reports ({filteredReports.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <BarChart3 className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No inventory reports found</p>
                          {canCreate('Inventory') && (
                            <Button onClick={() => handleGenerateReport('valuation')} size="sm">
                              Generate your first report
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          <div className="font-medium">{report.reportName}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getTypeLabel(report.reportType)}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(report.startDate).toLocaleDateString()}</div>
                            <div className="text-muted-foreground">
                              to {new Date(report.endDate).toLocaleDateString()}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(report.status)}
                        </TableCell>
                        <TableCell>
                          {report.summary && (
                            <div className="text-sm">
                              <div>Items: {report.summary.totalItems}</div>
                              <div className="text-muted-foreground">
                                Value: {formatCurrency(report.summary.totalValue)}
                              </div>
                              {report.summary.lowStockItems > 0 && (
                                <div className="text-orange-600">
                                  Low Stock: {report.summary.lowStockItems}
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(report.dateCreated).toLocaleDateString()}</div>
                            {report.generatedBy && (
                              <div className="text-muted-foreground">by {report.generatedBy}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <div className="h-4 w-4">⋮</div>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Inventory') && (
                                <DropdownMenuItem onClick={() => navigate(`/inventory/reports/${report.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Report
                                </DropdownMenuItem>
                              )}
                              {report.status === 'completed' && report.fileUrl && (
                                <DropdownMenuItem onClick={() => handleDownloadReport(report)}>
                                  <Download className="mr-2 h-4 w-4" />
                                  Download PDF
                                </DropdownMenuItem>
                              )}
                              {canCreate('Inventory') && (
                                <DropdownMenuItem onClick={() => handleGenerateReport(report.reportType)}>
                                  <BarChart3 className="mr-2 h-4 w-4" />
                                  Regenerate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryReports; 